import React from 'react';
import { formatPrice } from '../../../../utils';
import MotorBrandSettingsCard from '../MotorBrandSettingsCard';
import { useLanguage } from '../../../../i18n/LanguageContext';

const KatlamaliSettings = React.memo(({
  katlamaliExtras,
  setKatlamaliExtras,
  isKatlamaliMekanizmaCollapsed,
  setIsKatlamaliMekanizmaCollapsed,
  newKatlamaliMekanizma,
  setNewKatlamaliMekanizma,
  handleSaveSettings,
  isSavingSettings
}) => {
  const { t } = useLanguage();
  const [editingId, setEditingId] = React.useState(null);

  const handleAddOrUpdate = () => {
    if (!newKatlamaliMekanizma.type.trim() || !newKatlamaliMekanizma.price) return;

    const currentList = katlamaliExtras.mekanizmaList || [];
    let newList;

    if (editingId) {
      newList = currentList.map(m => m.id === editingId ? {
        ...m,
        type: newKatlamaliMekanizma.type.trim(),
        price: parseFloat(newKatlamaliMekanizma.price)
      } : m);
    } else {
      newList = [...currentList, {
        id: Date.now().toString(),
        type: newKatlamaliMekanizma.type.trim(),
        price: parseFloat(newKatlamaliMekanizma.price)
      }];
    }

    setKatlamaliExtras({ ...katlamaliExtras, mekanizmaList: newList });
    handleSaveSettings({ katlamaliMekanizmaList: newList });
    setNewKatlamaliMekanizma({ type: '', price: '' });
    setEditingId(null);
  };

  return (
    <>
      <MotorBrandSettingsCard
        title={t('productList.katlamaliSettings.motorTitle', 'Katlamalı Perde Motor ve Kumanda Ayarları')}
        subtitle={t('productList.katlamaliSettings.motorSubtitle', 'Katlamalı Perde siparişlerinde kullanılacak 3 motor firmasının motor ve kumanda ücretleri')}
        productType="katlamaliPerde"
        extras={katlamaliExtras}
        setExtras={setKatlamaliExtras}
        handleSaveSettings={handleSaveSettings}
        isSavingSettings={isSavingSettings}
      />

      <div className="settings-card settings-card--accordion">
        <div
          className="settings-accordion-header"
          onClick={() => setIsKatlamaliMekanizmaCollapsed(!isKatlamaliMekanizmaCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setIsKatlamaliMekanizmaCollapsed(!isKatlamaliMekanizmaCollapsed)}
        >
          <div className="settings-card-title">
            <i className="fas fa-tools"></i>
            <div className="settings-card-title-text">
              <h4>{t('productList.katlamaliSettings.title', 'Katlamalı Perde Mekanizma Ayarları')}</h4>
              <span className="settings-card-subtitle">
                {t('productList.katlamaliSettings.subtitle', 'Mekanizma tipleri ve ücretleri')}
              </span>
            </div>
          </div>
          <i className={`fas fa-chevron-${isKatlamaliMekanizmaCollapsed ? 'down' : 'up'} settings-accordion-chevron`}></i>
        </div>

        {!isKatlamaliMekanizmaCollapsed && (
          <div className="settings-accordion-content">
            <div className="mekanizma-add-form">
              <input
                type="text"
                placeholder={t('productList.katlamaliSettings.mechanismTypePlaceholder', 'Mekanizma Tipi (Örn: 5+1)')}
                value={newKatlamaliMekanizma.type}
                onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, type: e.target.value })}
              />
              <div className="settings-input-wrap settings-input-wrap--sm">
                <input
                  type="number"
                  placeholder={t('productList.katlamaliSettings.pricePlaceholder', 'Ücret')}
                  value={newKatlamaliMekanizma.price}
                  onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, price: e.target.value })}
                />
                <span className="settings-input-suffix">€</span>
              </div>
              <button type="button" className="mekanizma-add-btn" onClick={handleAddOrUpdate}>
                {editingId ? t('common.update', 'Güncelle') : t('common.add', 'Ekle')}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="mekanizma-cancel-btn"
                  onClick={() => {
                    setEditingId(null);
                    setNewKatlamaliMekanizma({ type: '', price: '' });
                  }}
                >
                  {t('common.cancel', 'Vazgeç')}
                </button>
              )}
            </div>

            {katlamaliExtras.mekanizmaList && katlamaliExtras.mekanizmaList.length > 0 ? (
              <>
                <p className="mekanizma-list-heading">
                  <span>{t('productList.katlamaliSettings.registeredMechanisms', 'Kayıtlı Mekanizma Tipleri')}</span>
                  <span className="mekanizma-list-count">{katlamaliExtras.mekanizmaList.length}</span>
                </p>
                <ul className="mekanizma-list">
                  {katlamaliExtras.mekanizmaList.map((mek, idx) => {
                    const isEditing = editingId === mek.id;

                    return (
                      <li
                        key={mek.id}
                        className={`mekanizma-row${isEditing ? ' mekanizma-row--editing' : ''}`}
                      >
                        <div className="mekanizma-row-main">
                          <span className="mekanizma-row-index" aria-hidden="true">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <div className="mekanizma-row-icon" aria-hidden="true">
                            <i className="fas fa-cog"></i>
                          </div>
                          <div className="mekanizma-row-details">
                            <span className="mekanizma-row-type">{mek.type}</span>
                            {isEditing && (
                              <span className="mekanizma-row-badge">
                                {t('productList.katlamaliSettings.editingBadge', 'Düzenleniyor')}
                              </span>
                            )}
                          </div>
                          <span className="mekanizma-row-price">{formatPrice(mek.price)} €</span>
                        </div>
                        <div className="mekanizma-row-actions">
                          <button
                            type="button"
                            className="mekanizma-icon-btn mekanizma-icon-btn--edit"
                            onClick={() => {
                              setEditingId(mek.id);
                              setNewKatlamaliMekanizma({ type: mek.type, price: mek.price });
                            }}
                            title={t('common.edit', 'Düzenle')}
                            aria-label={`${mek.type} - ${t('common.edit', 'Düzenle')}`}
                            disabled={isEditing}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            className="mekanizma-icon-btn mekanizma-icon-btn--delete"
                            onClick={() => {
                              const newList = katlamaliExtras.mekanizmaList.filter(m => m.id !== mek.id);
                              setKatlamaliExtras({ ...katlamaliExtras, mekanizmaList: newList });
                              handleSaveSettings({ katlamaliMekanizmaList: newList });
                              if (editingId === mek.id) {
                                setEditingId(null);
                                setNewKatlamaliMekanizma({ type: '', price: '' });
                              }
                            }}
                            title={t('common.delete', 'Sil')}
                            aria-label={`${mek.type} - ${t('common.delete', 'Sil')}`}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <div className="mekanizma-empty-state">
                <i className="fas fa-tools"></i>
                <p>{t('productList.katlamaliSettings.emptyTitle', 'Henüz mekanizma tipi eklenmemiş.')}</p>
                <span>{t('productList.katlamaliSettings.emptyDesc', 'Yukarıdaki formu kullanarak ilk mekanizmayı ekleyebilirsiniz.')}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
});

export default KatlamaliSettings;
